import { defineStore } from "pinia";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import { postRequest } from "@/services/backendApi";
import { API_BASE_URL } from "@/config";
import router from "@/router";
import { publishAuthChange, type AuthSyncEvent } from "@/utils/authSync";

type Impersonator = {
  idUser: number;
  email: string;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
};

type User = {
  idUser: number;
  userImage: string | null;
  userName: string;
  initials: string;
  title: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  mobile: string;
  landline: string;
  // Migration 0395: Mobilnummer geschäftlich (sonst privat).
  mobileIsBusiness?: boolean;
  idOrganization: number;
  organizationName: string;
  organizationRoles: string[];
  organizationLogo: string | null;
  userRole: string;
  email: string;
  impersonatedBy: Impersonator | null;
  accessTier: "full" | "light";
  idLightUserCase: number | null;
  lightUserCaseStatus: string | null;
  idLocation: number | null;
  // Abteilungen des Benutzers (Kundenfeedback 11.09.26): Fallback für die
  // Stellenbezeichnung in der Signatur und Erkennung der "Zentrale".
  departments?: { id: number; name: string }[];
  // Standard-Empfänger ohne Sachbearbeiter (Kommunikationseinstellungen,
  // Kundenfeedback 02.10.26, VI.): zählt zur „Zentrale".
  isFallbackRecipient?: boolean;
  // Demo-Modus des Super-Admin-Bereichs (internal/demo): Die aktive
  // Organisation ist eine Demo-Organisation, nichts geht nach außen.
  isDemo?: boolean;
};

export type LinkedOrg = {
  idOrganization: number;
  organizationName: string;
  organizationLogo: string | null;
  userRole: string;
  addedVia: "primary" | "link_existing" | "link_new" | string;
  isActive: boolean;
};

// Laufende /me/orgs-Abfrage je Identität (Benutzer:Organisation). Laufen
// Router-Guard und fetchUser gleichzeitig, teilen sie sich eine Anfrage.
let membershipsInFlight: { key: string; promise: Promise<void> } | null = null;

export const useAuthStore = defineStore("auth", {
  state: (): {
    user: User | null;
    linkedOrgs: LinkedOrg[];
  } => ({
    user: null,
    linkedOrgs: [],
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    idUser: (state) => (state.user ? state.user.idUser : ""),
    userImage: (state) => (state.user ? state.user.userImage : null),
    userName: (state) => (state.user ? state.user.userName : ""),
    initials: (state) => (state.user ? state.user.initials : ""),
    idOrganization: (state) => (state.user ? state.user.idOrganization : 0),
    organizationName: (state) => (state.user ? state.user.organizationName : ""),
    organizationRoles: (state) => (state.user ? state.user.organizationRoles : []),
    organizationLogo: (state) => (state.user ? state.user.organizationLogo : null),
    userRole: (state) => (state.user ? state.user.userRole : ""),
    email: (state) => (state.user ? state.user.email : ""),
    title: (state) => (state.user ? state.user.title : ""),
    // Stellenbezeichnung fuer den Signatur-Platzhalter {{stellenbezeichnung}}.
    jobTitle: (state) => (state.user ? state.user.jobTitle : ""),
    departments: (state) => state.user?.departments ?? [],
    departmentName: (state) => state.user?.departments?.[0]?.name ?? "",
    // Signatur-Zeile unter dem Namen: Stellenbezeichnung, ersatzweise die
    // Abteilung (z. B. "Serviceabteilung") – nie ein erfundener Beispielwert
    // (Kundenfeedback 11.09.26, I.).
    signatureJobTitle: (state) =>
      (state.user?.jobTitle || "").trim() || state.user?.departments?.[0]?.name || "",
    // "Zentrale" im Sinne des Cockpits: Administratoren, Unternehmensleiter
    // und Mitglieder einer Abteilung namens "Zentrale" sehen neben den
    // eigenen ungelesenen Nachrichten auch den unzugeordneten Posteingang
    // der Organisation (Kundenfeedback 11.09.26, II.).
    // Standard-Empfänger ohne Sachbearbeiter (Kommunikationseinstellungen)
    // gehören ebenfalls dazu – identisch zu responsibility.IsCentralUser.
    isCentralInboxUser: (state) => {
      const role = state.user?.userRole;
      if (role === "Administrator" || role === "Unternehmensleiter" || role === "SuperAdmin") return true;
      if (state.user?.isFallbackRecipient) return true;
      return (state.user?.departments ?? []).some((d) => /^zentrale$/i.test((d.name || "").trim()));
    },
    firstName: (state) => (state.user ? state.user.firstName : ""),
    lastName: (state) => (state.user ? state.user.lastName : ""),
    mobile: (state) => (state.user ? state.user.mobile : ""),
    landline: (state) => (state.user ? state.user.landline : ""),
    // {{mobil}} in Signaturen: nur eine geschäftliche Mobilnummer, eine
    // private bliebe sonst in jeder E-Mail nach außen (Kundenfeedback 26.10.2026).
    mobileIsBusiness: (state) => state.user?.mobileIsBusiness === true,
    signatureMobile: (state) => (state.user?.mobileIsBusiness ? state.user.mobile : ""),
    idLocation: (state) => (state.user ? state.user.idLocation : null),

    isAdministrator: (state) => state.user?.userRole === "Administrator",
    isCompanyManager: (state) => state.user?.userRole === "Unternehmensleiter",
    isLocationManager: (state) => state.user?.userRole === "Standortleiter",
    isEmployee: (state) => state.user?.userRole === "Mitarbeiter",
    isSuperAdmin: (state) => state.user?.userRole === "SuperAdmin",

    isPrivatePerson: (state) => state.user?.organizationRoles?.includes("Privatkunde") || state.user?.organizationRoles?.includes("Zeuge"),

    // Werkstatt-Klassifikation: jede Rolle mit "Autohaus" oder "Werkstatt".
    // Kein Vorrang mehr für Anwalt/Kanzlei — Rechte werden vereinigt
    // (Design Mehrfachrollen 11.09.2026), konsistent zu form.HasRoleClass.
    isWorkshop: (state) => {
      const roles = state.user?.organizationRoles || [];
      return roles.some((r) => /autohaus|werkstatt/i.test(r));
    },

    isLightUser: (state) => state.user?.accessTier === "light",
    idLightUserCase: (state) => state.user?.idLightUserCase ?? null,
    lightUserCaseStatus: (state) => state.user?.lightUserCaseStatus ?? null,
    lightUserMustFillQuestionnaire: (state) =>
      state.user?.accessTier === "light" && state.user?.lightUserCaseStatus === "Fallaufnahme",

    isImpersonating: (state) => !!state.user?.impersonatedBy,
    isDemo: (state) => state.user?.isDemo === true,
    impersonator: (state) => state.user?.impersonatedBy ?? null,

    // Cross-org context (#418): expose whether the user is currently acting
    // inside a switched (non-primary) organization so the UI can surface it.
    activeLinkedOrg: (state) => state.linkedOrgs.find((o) => o.isActive) ?? null,
    primaryLinkedOrg: (state) => state.linkedOrgs.find((o) => o.addedVia === "primary") ?? null,
    isInSwitchedOrg(): boolean {
      const active = this.activeLinkedOrg;
      return !!active && active.addedVia !== "primary";
    },
  },

  actions: {
    async fetchUser(): Promise<void> {
      try {
        const data: {
          id: number;
          userImage: string | null;
          userName: string;
          initials: string;
          title: string;
          jobTitle: string;
          departments?: { id: number; name: string }[];
          firstName: string;
          lastName: string;
          mobile: string;
          landline: string;
          mobileIsBusiness?: boolean;
          idOrganization: number;
          organizationName?: string;
          organizationLogo: string | null;
          organizationRoles: string[];
          userRole: string;
          email: string;
          impersonatedBy?: Impersonator | null;
          accessTier?: "full" | "light";
          idLightUserCase?: number | null;
          lightUserCaseStatus?: string | null;
          isFallbackRecipient?: boolean;
          isDemo?: boolean;
        } = await getRequestGeneric("/protected/users/check-session");

        // Identitätswechsel (Login, Org-Wechsel, Impersonation) an andere
        // Tabs melden — dort wird ein veralteter Anmeldezustand sonst weiter
        // angezeigt, obwohl der gemeinsame Session-Cookie längst zu einer
        // anderen Identität gehört.
        const prevIdUser = this.user?.idUser ?? null;
        const prevIdOrganization = this.user?.idOrganization ?? null;

        this.user = {
          idUser: data.id,
          userImage: data.userImage,
          userName: data.userName,
          initials: data.initials,
          title: data.title || "",
          jobTitle: data.jobTitle || "",
          departments: Array.isArray(data.departments) ? data.departments : [],
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          mobile: data.mobile || "",
          landline: data.landline || "",
          mobileIsBusiness: data.mobileIsBusiness === true,
          idOrganization: data.idOrganization,
          organizationName: data.organizationName || "",
          organizationLogo: data.organizationLogo,
          organizationRoles: data.organizationRoles || [],
          userRole: data.userRole,
          email: data.email,
          impersonatedBy: data.impersonatedBy ?? null,
          accessTier: data.accessTier ?? "full",
          idLightUserCase: data.idLightUserCase ?? null,
          lightUserCaseStatus: data.lightUserCaseStatus ?? null,
          idLocation: (data as any).idLocation ?? null,
          isFallbackRecipient: data.isFallbackRecipient === true,
          isDemo: data.isDemo === true,
        };
        if (prevIdUser !== this.user.idUser || prevIdOrganization !== this.user.idOrganization) {
          publishAuthChange({
            type: "identity",
            idUser: this.user.idUser,
            idOrganization: this.user.idOrganization,
          });
        }

        // Load cross-org memberships in the background. Admin-only inside
        // the action; ignore failures so a missing backend never blocks login.
        void this.fetchMemberships();
      } catch (error: unknown) {
        if (error instanceof Error) {
          throw error;
        } else {
          throw new Error("fetchUser failed");
        }
      }
    },

    // Special version for router guard that doesn't trigger auto-logout redirect
    async fetchUserForRouterGuard(): Promise<void> {
      try {
        const response = await fetch(`${API_BASE_URL}/protected/users/check-session`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          // Throw error with status code for router guard to handle
          throw { errorCode: response.status, message: "Not authenticated" };
        }

        const data: {
          id: number;
          userImage: string | null;
          userName: string;
          initials: string;
          title: string;
          jobTitle: string;
          departments?: { id: number; name: string }[];
          firstName: string;
          lastName: string;
          mobile: string;
          landline: string;
          mobileIsBusiness?: boolean;
          idOrganization: number;
          organizationName?: string;
          organizationLogo: string | null;
          organizationRoles: string[];
          userRole: string;
          email: string;
          impersonatedBy?: Impersonator | null;
          accessTier?: "full" | "light";
          idLightUserCase?: number | null;
          lightUserCaseStatus?: string | null;
          isFallbackRecipient?: boolean;
          isDemo?: boolean;
        } = await response.json();

        this.user = {
          idUser: data.id,
          userImage: data.userImage,
          userName: data.userName,
          initials: data.initials,
          title: data.title || "",
          jobTitle: data.jobTitle || "",
          departments: Array.isArray(data.departments) ? data.departments : [],
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          mobile: data.mobile || "",
          landline: data.landline || "",
          mobileIsBusiness: data.mobileIsBusiness === true,
          idOrganization: data.idOrganization,
          organizationName: data.organizationName || "",
          organizationLogo: data.organizationLogo,
          organizationRoles: data.organizationRoles || [],
          userRole: data.userRole,
          email: data.email,
          impersonatedBy: data.impersonatedBy ?? null,
          accessTier: data.accessTier ?? "full",
          idLightUserCase: data.idLightUserCase ?? null,
          lightUserCaseStatus: data.lightUserCaseStatus ?? null,
          idLocation: (data as any).idLocation ?? null,
          isFallbackRecipient: data.isFallbackRecipient === true,
          isDemo: data.isDemo === true,
        };

        // Wie fetchUser: Mitgliedschaften im Hintergrund laden. Sonst bleibt
        // linkedOrgs nach einem vollständigen Seitenaufruf leer und der
        // Unternehmens-Wechsler fehlt.
        void this.fetchMemberships();
      } catch (error: any) {
        // Re-throw with error code for router guard
        throw { errorCode: error?.errorCode || 500, message: error?.message || "fetchUser failed" };
      }
    },

    async logout() {
      try {
        await postRequest("/protected/users/logout", null, undefined, true);
      } catch {
        console.warn("Logout: Logout failed or session already gone.");
      }

      this.user = null;
      this.linkedOrgs = [];
      // Andere offene Fenster teilen sich denselben (jetzt beendeten)
      // Session-Cookie und müssen ihren Anmeldezustand ebenfalls verwerfen.
      publishAuthChange({ type: "logout" });
      // Reset onboarding state lazily to avoid an import cycle at module load.
      try {
        const { useOnboardingStore } = await import("@/stores/onboardingStore");
        useOnboardingStore().reset();
      } catch { /* no-op */ }
      router.push({ name: "PageLogin" });
    },

    /**
     * Reaktion auf eine Anmeldeänderung aus einem anderen Tab/Fenster
     * (siehe utils/authSync.ts; registriert in App.vue). Da sich alle
     * Fenster den Session-Cookie teilen, ist der lokale In-Memory-Zustand
     * nach einem fremden Logout/Login/Org-Wechsel veraltet.
     */
    handleExternalAuthChange(event: AuthSyncEvent): void {
      const current = this.user;
      // Ohne lokalen Anmeldezustand (z. B. Login-Seite) gibt es nichts zu
      // invalidieren.
      if (!current) return;

      if (event.type === "logout") {
        this.user = null;
        this.linkedOrgs = [];
        // Harter Wechsel statt SPA-Navigation: Feature-Stores dürfen keine
        // Daten der beendeten Session im Speicher behalten.
        window.location.assign(router.resolve({ name: "PageLogin" }).fullPath);
        return;
      }

      if (current.idUser !== event.idUser || current.idOrganization !== event.idOrganization) {
        // Neue Identität im gemeinsamen Cookie: kompletter Neuaufbau dieses
        // Fensters; der Router-Guard lädt danach den frischen Zustand.
        // Bewusste Abwägung: Der Reload verwirft ungespeicherte Eingaben
        // (kein performGuardedAction-Dialog). Weiterarbeiten wäre gefährlicher —
        // jeder Speichern-Klick würde die Daten unter der NEUEN Session in den
        // falschen Mandanten schreiben.
        window.location.reload();
      }
    },

    /**
     * Gleicht die Server-Session mit dem In-Memory-Zustand dieses Fensters
     * ab (aufgerufen bei Fenster-Fokus, siehe App.vue). Fängt Fälle ab, die
     * die Tab-Synchronisation nicht erreicht (z. B. Abmeldung vor deren
     * Einführung oder ein abgelaufener Session-Cookie).
     */
    async revalidateSession(): Promise<void> {
      if (!this.user) return;
      try {
        const response = await fetch(`${API_BASE_URL}/protected/users/check-session`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (response.status === 401 || response.status === 403) {
          // Session wurde beendet (anderes Fenster/Ablauf): Zustand verwerfen.
          this.user = null;
          this.linkedOrgs = [];
          window.location.assign(router.resolve({ name: "PageLogin" }).fullPath);
          return;
        }
        if (!response.ok) return; // Serverfehler: keinen Reload erzwingen

        const data = await response.json();
        const sameIdentity =
          Number(data?.id) === this.user?.idUser &&
          Number(data?.idOrganization) === this.user?.idOrganization;
        if (!sameIdentity) {
          window.location.reload();
        }
      } catch {
        /* Netzfehler: still bleiben, nächster Fokus prüft erneut */
      }
    },

    /**
     * Loads the list of organizations the current user is a member of
     * (primary + cross-org links). Soft-fails so a missing backend never
     * breaks login. Nicht mehr Admin-only: auch Standortleiter/Mitarbeiter
     * mit mehreren Unternehmen brauchen den Wechsler (Kundenreview
     * 23.08.26, XXI); das Backend prüft die Mitgliedschaft.
     */
    async fetchMemberships(): Promise<void> {
      if (!this.user) {
        this.linkedOrgs = [];
        return;
      }
      const key = `${this.user.idUser}:${this.user.idOrganization}`;
      if (membershipsInFlight?.key === key) {
        return membershipsInFlight.promise;
      }
      const promise = this.loadMemberships().finally(() => {
        if (membershipsInFlight?.promise === promise) membershipsInFlight = null;
      });
      membershipsInFlight = { key, promise };
      return promise;
    },

    /** Eigentliche Abfrage; Aufrufer nutzen fetchMemberships (bündelt parallele Aufrufe). */
    async loadMemberships(): Promise<void> {
      try {
        const data = await getRequestGeneric("/protected/users/me/orgs");
        // Das Backend antwortet mit { memberships, activeIdOrganization }
        // (internal/tenants/orglinks/handler.go). Ein blankes Array wird
        // weiterhin akzeptiert, damit ein aelterer Backend-Stand den
        // Wechsler nicht stillschweigend leert.
        let raw: any[] = [];
        if (Array.isArray(data)) raw = data;
        else if (Array.isArray(data?.memberships)) raw = data.memberships;
        // isActive liefert das Backend nicht je Mitgliedschaft mit; es ergibt
        // sich aus der aktiven Organisation der Sitzung.
        const activeId = Number(data?.activeIdOrganization) || this.idOrganization || 0;
        this.linkedOrgs = raw.map((m: any): LinkedOrg => ({
          idOrganization: Number(m.idOrganization),
          organizationName: m.organizationName || "",
          organizationLogo: m.organizationLogo ?? null,
          userRole: m.userRole || "",
          addedVia: m.addedVia || "",
          isActive: Number(m.idOrganization) === activeId,
        }));
      } catch {
        this.linkedOrgs = [];
      }
    },

    /**
     * Switches the active organization for an admin who is linked to
     * multiple organizations. Returns true on success. Re-fetches the user
     * so the rest of the app sees the new org context immediately.
     */
    async switchOrg(idOrganization: number, redirectTo = "/dashboard"): Promise<boolean> {
      if (!this.user) return false;
      try {
        // postRequest wirft ohne errorHandler NICHT bei HTTP-Fehlern — die
        // Antwort muss auf das error-Feld geprüft werden (Backend:
        // response.Error → {"error": …}). Sonst würde ein abgelehnter
        // Wechsel stillschweigend als Erfolg behandelt und trotzdem
        // weitergeleitet.
        const resp = await postRequest("/protected/users/switch-org", { idOrganization }, undefined, true);
        if (!resp || resp.error) {
          const { useSnackbarStore } = await import("@/stores/snackbarStore");
          useSnackbarStore().showError("Der Unternehmenswechsel ist fehlgeschlagen. Bitte laden Sie die Seite neu und versuchen Sie es erneut.");
          return false;
        }
        await this.fetchUser();
        await this.fetchMemberships();
        // Hard reload so cached lists/permissions in feature stores are clean.
        window.location.assign(redirectTo);
        return true;
      } catch (e) {
        console.warn("switchOrg failed", e);
        return false;
      }
    },

    // Ends an active impersonation and refetches the user. After Stop the
    // backend has restored the SuperAdmin's original session cookie (or
    // invalidated it if it was no longer valid - in that case fetchUser
    // will throw and we fall back to the login page).
    async stopImpersonating(): Promise<void> {
      // Aus dem Demo-Modus zurück auf die Demo-Seite, sonst in die Benutzerliste.
      const target = this.user?.isDemo ? "AdminDemo" : "AdminUsers";
      try {
        await postRequest("/protected/users/stop-impersonating", null, undefined, true);
      } catch (e) {
        console.warn("stopImpersonating: backend call failed", e);
      }

      try {
        await this.fetchUser();
        router.push({ name: target });
      } catch {
        this.user = null;
        router.push({ name: "PageLogin" });
      }
    },
  },
});
